import { Box, Flex, Stack, Text, Divider, Group, Space, SimpleGrid } from '@mantine/core'
import Image from 'next/image'
import React from 'react'
import ThickDivider from './shared/ThickDivider'
import { WrapperLink } from '@/components/WrapperLink'
import { appPath } from '@/lib/getPublicAppUrl'

const footerNavigation = [
    {
        title: 'Get care',
        links: [
            { text: 'Store', href: '/store/home' },
            { text: 'Everyday care', href: '/store/everyday-care' },
            { text: 'Specialty care', href: '/store/specialty-care' },
            { text: 'Medications', href: '/store/medications' },
            { text: 'Lab tests', href: '/store/labs' },
            { text: 'All care', href: '/all-care' },
        ],
    },
    {
        title: 'Company',
        links: [
            { text: 'About', href: 'https://generalmedicine.co/company/about/' },
            { text: 'Careers', href: 'https://generalmedicine.co/company/careers/' },
            { text: 'Resources', href: 'https://generalmedicine.co/resources/' },
        ],
    },
    {
        title: 'Legal',
        links: [
            { text: 'Terms of service', href: 'https://generalmedicine.co/terms-of-service' },
            { text: 'Privacy policy', href: 'https://generalmedicine.co/privacy-policy' },
            { text: 'HIPAA NPP', href: 'https://generalmedicine.co/hipaa-privacy-practices' },
        ],
    },
    {
        title: 'Find us',
        links: [
            { text: 'Instagram', href: 'https://www.instagram.com/generalmedicineco' },
            { text: 'Facebook', href: 'https://www.facebook.com/generalmedicineco' },
            { text: 'LinkedIn', href: 'https://www.linkedin.com/company/general-medicine' },
            { text: 'X (Twitter)', href: 'https://x.com/GeneralMedicine' },
        ],
    },
]

const contactInfo = [
    '128 Spear St. Floor 4',
    'San Francisco, California, 94105',
    '(801) 251-6055',
    { text: 'hello@generalmedicine.co', href: 'mailto:hello@generalmedicine.co' },
]

export function GeneralMedicineFooter() {
    return (
        <>
            <Space h={{ base: 40, sm: 100 }} />
            <Box hiddenFrom="sm">
                <ThickDivider />
            </Box>
            <Box visibleFrom="sm">
                <Divider />
            </Box>
            <Box hiddenFrom="md">
                <MobileFooter />
            </Box>
            <Box visibleFrom="md">
                <DesktopFooter />
            </Box>
        </>
    )
}

function NavItem({ title, children }: { title: string; children: React.ReactNode }) {
    return (
        <Box
            component="details"
            style={{
                border: 'none',
                outline: 'none',
            }}
            data-testid={`footer-nav-item-${title.toLowerCase().replace(/\s+/g, '-')}`}
        >
            <Flex
                component="summary"
                justify="space-between"
                align="center"
                py="xs"
                className="footer-accordion-summary"
                style={{
                    cursor: 'pointer',
                }}
            >
                <Text fz={16} fw="var(--gm-weight-text-bold)" c="black">
                    {title}
                </Text>
                <Text
                    fz={18}
                    c="gray.7"
                    fw="bold"
                    className="footer-accordion-icon"
                    style={{
                        userSelect: 'none',
                    }}
                />
            </Flex>
            <Stack gap="xs" pb="xs" data-testid={`footer-nav-content-${title.toLowerCase().replace(/\s+/g, '-')}`}>
                {children}
            </Stack>
        </Box>
    )
}

function FooterLink({ href, children }: { href: string; children: React.ReactNode }) {
    return (
        <Text component="a" href={appPath(href)} c="gray.9" fz={16} style={{ textDecoration: 'none' }}>
            {children}
        </Text>
    )
}

function MobileFooter() {
    return (
        <Box component="footer" py="md" px="lg" bg="var(--gm-color-background-primary)" data-testid="mobile-footer">
            <Stack gap="xs">
                {/* Navigation Links */}
                {footerNavigation.map((section, index) => (
                    <React.Fragment key={section.title}>
                        <NavItem title={section.title}>
                            {section.links.map((link) => (
                                <Text key={link.text} component="a" href={appPath(link.href)} c="gray.9" fz="md">
                                    {link.text}
                                </Text>
                            ))}
                        </NavItem>
                        {index < footerNavigation.length - 1 && <Divider />}
                    </React.Fragment>
                ))}
                <Divider />

                {/* Contact Information + LegitScript Image */}
                <Flex align="flex-start" mt="xs" mb="xl" data-testid="mobile-footer-contact-section">
                    <Stack gap={4}>
                        {contactInfo.map((item, index) =>
                            typeof item === 'string' ? (
                                <Text key={index} fz="md" c="gray.9">
                                    {item}
                                </Text>
                            ) : (
                                <Text
                                    key={index}
                                    component="a"
                                    href={appPath(item.href)}
                                    c="gray.9"
                                    fz="md"
                                    data-testid="mobile-footer-contact-email"
                                >
                                    {item.text}
                                </Text>
                            ),
                        )}
                    </Stack>
                    <Box ml="auto">
                        <a
                            href="https://www.legitscript.com/websites/generalmedicine.co/"
                            target="_blank"
                            rel="noopener noreferrer"
                            data-testid="mobile-footer-legitscript-link"
                        >
                            <Image src={appPath('/legitscript.svg')} alt="LegitScript Certified" height={72} width={72} />
                        </a>
                    </Box>
                </Flex>

                {/* Logo and Copyright */}
                <Flex justify="space-between" align="center" data-testid="mobile-footer-logo-section">
                    <Box>
                        <Flex align="center" gap="xs">
                            <img src={appPath('/logo-gm.svg')} alt="Logo" height={35} data-testid="mobile-footer-logo" />
                        </Flex>
                        <Space h={4} />
                        <Text fz="md" c="var(--gm-foreground-fg-tertiary)" fw={500} mt={4}>
                            © {new Date().getFullYear()} Path Healthcare Systems Corp.
                        </Text>
                    </Box>
                </Flex>
            </Stack>
        </Box>
    )
}

function DesktopFooter() {
    return (
        <Box component="footer" py={40} pl="xl" bg="var(--gm-color-background-primary)" data-testid="desktop-footer">
            <Flex justify="space-between">
                {/* Logo and Copyright - Left column */}
                <Group
                    justify="flex-start"
                    align="flex-start"
                    w="25%"
                    ml="xl"
                    data-testid="desktop-footer-logo-section"
                >
                    <Box mr="sm">
                        <Flex direction="column">
                            <img src={appPath('/logo-gm.svg')} alt="Logo" height={51} data-testid="desktop-footer-logo" />
                            <Space h={16} />
                            <Text fz={16} c="var(--gm-foreground-fg-tertiary)" fw={500} mt={4}>
                                © {new Date().getFullYear()} Path Healthcare Systems Corp.
                            </Text>
                        </Flex>
                    </Box>
                </Group>

                {/* Main Content - Right column */}
                <Group justify="flex-end" align="flex-end" w="65%" data-testid="desktop-footer-content">
                    <Box w="100%">
                        {/* Four link columns across in a single row */}
                        <SimpleGrid cols={4} spacing="md">
                            {footerNavigation.map((category) => (
                                <Stack key={category.title} gap="xs">
                                    <Text fz={16} fw={600} c="gray.9">
                                        {category.title}
                                    </Text>
                                    <Stack gap={6}>
                                        {category.links.map((link) => (
                                            <FooterLink key={link.text} href={link.href}>
                                                {link.text}
                                            </FooterLink>
                                        ))}
                                    </Stack>
                                </Stack>
                            ))}
                        </SimpleGrid>

                        {/* Bottom row: HQ, contact, LegitScript */}
                        <Flex direction="row" wrap="nowrap" mt={40} align="flex-start">
                            <Box style={{ flex: '1 1 25%' }} data-testid="desktop-footer-hq-section">
                                <Text fz={16} fw={600} c="gray.9">
                                    General Medicine HQ
                                </Text>
                            </Box>

                            <Box style={{ flex: '1 1 50%' }} data-testid="desktop-footer-contact-section">
                                <Stack gap={6}>
                                    {contactInfo.map((item, index) =>
                                        typeof item === 'string' ? (
                                            <Text key={index} fz={16} c="gray.9">
                                                {item}
                                            </Text>
                                        ) : (
                                            <Text
                                                key={index}
                                                component="a"
                                                href={appPath(item.href)}
                                                c="gray.9"
                                                fz={16}
                                                style={{ textDecoration: 'none' }}
                                                data-testid="desktop-footer-contact-email"
                                            >
                                                {item.text}
                                            </Text>
                                        ),
                                    )}
                                </Stack>
                            </Box>

                            <Box style={{ flex: '0 0 auto' }} data-testid="desktop-footer-legitscript-section">
                                <WrapperLink
                                    href="https://legitscript.com/websites/generalmedicine.co"
                                    target="_blank"
                                    openExternally
                                    data-testid="desktop-footer-legitscript-link"
                                >
                                    <Box>
                                        <Image
                                            src={appPath('/legitscript.svg')}
                                            alt="LegitScript Certified"
                                            height={90}
                                            width={90}
                                        />
                                    </Box>
                                </WrapperLink>
                            </Box>
                        </Flex>
                    </Box>
                </Group>
            </Flex>
        </Box>
    )
}
